import React, { useMemo } from "react";
import "./../Components/QuestionCard/QuestionCard.scss";
import { Dropdown, DropdownToggleType } from "../Components";
import { NestedInput, NestedOptionsConfigMap } from "./NestedInput";
import * as R from "ramda";
import { Form } from "react-bootstrap";
import { StateSetter } from "../utils/types";

export enum QuestionCardType {
  RADIO = "radio",
  DROPDOWN = "dropdown",
  CHECKBOX = "checkbox",
}

export type OnChange = (value: any, path?: string, helper?: OnChangeHelper, ...args: any) => void;
export type OnChangeHelper = <T>(value: any, state: T) => T;
export type CheckOption = (value?: any, state?: any) => boolean | string;

export interface QuestionProps {
  title?: string;
  subtitle?: string;
  type: QuestionCardType;
  options?: Array<{ value: string | number | boolean; label: string }>;
  readOnly?: boolean;
  nestedOptionsConfig?: NestedOptionsConfigMap;
  onChange: OnChange | StateSetter<any>;
  className?: string;
  formState: any;
  formStateKey: string | null;
  checkOption?: CheckOption;
  onChangeHelper?: OnChangeHelper;
  alwasyShowNestedQuestion?: boolean;
  note?: string | string[];
}

export const Question: React.FC<QuestionProps> = ({
  title,
  subtitle,
  type,
  options = [],
  readOnly = false,
  nestedOptionsConfig = {},
  onChange,
  formState,
  formStateKey,
  checkOption,
  onChangeHelper,
  alwasyShowNestedQuestion,
  note,
}) => {
  const question = useMemo(() => {
    const makeNestedInput = (value, nestedOptionsConfig, isChecked, show) => {
      return !R.isNil(nestedOptionsConfig[`${value}`]) && (isChecked || show) ? (
        <NestedInput
          key={`${type}NestedInput${value}`}
          config={nestedOptionsConfig[`${value}`]}
          readOnly={readOnly}
          onChange={onChange}
          formState={
            nestedOptionsConfig[`${value}`].formState !== undefined
              ? nestedOptionsConfig[`${value}`].formState
              : formState
          }
          disabled={!isChecked}
        />
      ) : null;
    };
    switch (type) {
      case QuestionCardType.RADIO:
        return (
          <div className={`questionInputfields ${readOnly ? "readOnly" : ""}`}>
            {options.map((option, index) => {
              const { value, label } = option;
              const isChecked = R.isNil(formStateKey)
                ? formState === value
                : formState[formStateKey] === value;
              const resolvedOnchange = R.isNil(formStateKey)
                ? () => onChange(value)
                : () => onChange(value, formStateKey, onChangeHelper);
              return (
                <div key={`radioOption${index}`} className="radioOption">
                  <Form.Check
                    type="radio"
                    id={`${value}-radio`}
                    label={label}
                    checked={isChecked}
                    onChange={resolvedOnchange}
                    disabled={readOnly}
                  />
                  {makeNestedInput(value, nestedOptionsConfig, isChecked, alwasyShowNestedQuestion)}
                </div>
              );
            })}
          </div>
        );
      case QuestionCardType.DROPDOWN: {
        const value = R.isNil(formStateKey) ? formState : formState[formStateKey];
        const resolvedOnchange = R.isNil(formStateKey)
          ? () => onChange(value)
          : () => onChange(value, formStateKey, onChangeHelper);
        return (
          <div className={`questionInputfields ${readOnly ? "readOnly" : ""}`}>
            <Dropdown
              options={options}
              value={value}
              type={DropdownToggleType.OUTLINED}
              disabled={readOnly}
              onChange={resolvedOnchange}
            />
            {makeNestedInput(
              value,
              nestedOptionsConfig,
              nestedOptionsConfig[value],
              alwasyShowNestedQuestion
            )}
          </div>
        );
      }
      case QuestionCardType.CHECKBOX: {
        return (
          <div className={`questionInputfields ${readOnly ? "readOnly" : ""}`}>
            <Form.Group key={"checkboxinputgroup"}>
              {options.map((option, index) => {
                const { value, label } = option;
                const isChecked = Boolean(
                  checkOption
                    ? checkOption(value)
                    : R.isNil(formStateKey)
                    ? formState === value
                    : formState[formStateKey] === value
                );
                const resolvedOnchange = R.isNil(formStateKey)
                  ? () => onChange(value)
                  : () => onChange(value, formStateKey, onChangeHelper);
                return (
                  <div key={`checkboxOption${index}`} className="checkboxOption">
                    <Form.Check
                      type="checkbox"
                      id={`${value}-checkbox`}
                      label={label}
                      checked={isChecked}
                      onChange={resolvedOnchange}
                      disabled={readOnly}
                    />
                    {makeNestedInput(
                      value,
                      nestedOptionsConfig,
                      isChecked,
                      alwasyShowNestedQuestion
                    )}
                  </div>
                );
              })}
            </Form.Group>
          </div>
        );
      }
      default:
        return null;
    }
  }, [
    formState,
    formStateKey,
    nestedOptionsConfig,
    onChange,
    options,
    readOnly,
    type,
    checkOption,
    onChangeHelper,
    alwasyShowNestedQuestion,
  ]);

  return (
    <div className="cardContent">
      <div className="questionTitle">{title}</div>
      {subtitle && <p className="cardSubtitle">{subtitle}</p>}
      {question}
      {note && (
        <div className="nestedInputNote">
          {Array.isArray(note)
            ? note.map(n => (
                <div key={`noteItem${title}${n}`} className="nestedNoteItem">
                  {n}
                </div>
              ))
            : note}
        </div>
      )}
    </div>
  );
};
